import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../constants";
import { Lock, KeyRound, Loader2, ArrowLeft } from "lucide-react";

// Coming-soon overlay for the B2B buying pages. Wraps the real page and,
// while the backend flag ORGS_COMING_SOON is on and this browser doesn't
// have a valid unlock key, renders a blurred backdrop + centre card. The
// real page is still MOUNTED underneath (so it stays visible & pricing is
// legible through the blur) but the overlay swallows all pointer events.
//
// Louise's private bypass lives ONLY in backend/.env as
// ORGS_COMING_SOON_BYPASS_CODE — never in source or in the frontend build.
// The frontend just POSTs the entered code to /api/orgs/coming-soon-bypass;
// on 200 we store a random `unlock_key` returned by the server in this
// browser's localStorage and drop the overlay for future visits.

const UNLOCK_KEY_STORAGE = "orgs_preview_unlock_key";

export default function ComingSoonGate({ children }) {
  const [state, setState] = useState({ phase: "checking" }); // checking | gated | open

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/orgs/coming-soon`);
        if (cancelled) return;
        const enabled = !!r?.data?.enabled;
        if (!enabled) return setState({ phase: "open" });
        const key = safeGet(UNLOCK_KEY_STORAGE);
        if (key) return setState({ phase: "open", unlocked: true });
        setState({ phase: "gated" });
      } catch {
        // If the flag endpoint fails, fail-open so the site still works.
        if (!cancelled) setState({ phase: "open" });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state.phase === "checking") {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#B89823]" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      {children}
      {state.unlocked && (
        <button
          onClick={() => { safeRemove(UNLOCK_KEY_STORAGE); setState({ phase: "gated" }); }}
          data-testid="orgs-restore-overlay"
          className="fixed bottom-3 right-3 z-[60] text-[11px] px-3 py-1.5 rounded-full bg-[#4A4A4A] text-white/90 hover:bg-black shadow-lg"
        >
          Return to public preview
        </button>
      )}
      {state.phase === "gated" && <ComingSoonOverlay onUnlock={(key) => { safeSet(UNLOCK_KEY_STORAGE, key); setState({ phase: "open", unlocked: true }); }} />}
    </div>
  );
}

function ComingSoonOverlay({ onUnlock }) {
  const [showCodeBox, setShowCodeBox] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e && e.preventDefault();
    if (!code.trim()) { setErr("Please enter the test code."); return; }
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/orgs/coming-soon-bypass`, { code: code.trim() });
      const key = r?.data?.unlock_key;
      if (!key) throw new Error("no key");
      onUnlock(key);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Unable to verify that code.");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: "rgba(253,251,247,0.55)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
      data-testid="orgs-coming-soon-overlay"
    >
      <div className="max-w-md w-full bg-white/95 rounded-3xl border-2 border-[#E8E5DC] p-6 sm:p-8 text-center shadow-2xl">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFF7D6] border border-[#E8D4A8] text-[10px] uppercase tracking-widest text-[#5A4A32] mb-4">
          <Lock className="w-3 h-3" /> Coming soon
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#4A4A4A] mb-3" data-testid="orgs-coming-soon-title">COMING SOON</h1>
        <p className="text-sm text-[#6B6B6B] leading-relaxed mb-2">Poodle Talk memberships for hotels, schools and organisations are currently being prepared.</p>
        <p className="text-sm text-[#6B6B6B] leading-relaxed mb-6">Our gentle AI companion experience will be available soon.</p>
        <a
          href="/"
          data-testid="orgs-coming-soon-return"
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#4A4A4A] text-white text-sm font-semibold hover:bg-black"
        >
          <ArrowLeft className="w-4 h-4" /> Return to Poodle Talk
        </a>

        <div className="mt-8 pt-4 border-t border-[#F0EDE3]">
          {!showCodeBox && (
            <button
              onClick={() => setShowCodeBox(true)}
              data-testid="orgs-coming-soon-admin-link"
              className="text-[11px] text-[#9A9A9A] hover:text-[#6B6B6B] underline underline-offset-2"
            >
              Admin testing
            </button>
          )}
          {showCodeBox && (
            <form onSubmit={submit} className="text-left space-y-2 mt-1" data-testid="orgs-coming-soon-form">
              <label className="block text-[10px] uppercase tracking-wide text-[#6B6B6B] font-semibold">Test code</label>
              <input
                type="password"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus
                autoComplete="off"
                data-testid="orgs-coming-soon-input"
                className="w-full px-3 py-2 rounded-xl border-2 border-[#E0E0E0] focus:border-[#B89823] outline-none text-sm font-mono"
              />
              {err && <div className="bg-[#FFF0F0] border border-[#F5D0D0] rounded-xl p-2 text-[11px] text-[#A55B64]" data-testid="orgs-coming-soon-error">{err}</div>}
              <div className="flex gap-2 pt-1">
                <button type="submit" disabled={busy} data-testid="orgs-coming-soon-submit" className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[#E8D4A8] text-[#5A4A32] text-xs font-semibold hover:bg-[#D9C599] disabled:opacity-50">
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <KeyRound className="w-3 h-3" />} Unlock
                </button>
                <button type="button" onClick={() => { setShowCodeBox(false); setCode(""); setErr(""); }} className="text-xs text-[#6B6B6B] hover:text-[#4A4A4A] underline underline-offset-2">Cancel</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function safeGet(k) { try { return localStorage.getItem(k) || ""; } catch { return ""; } }
function safeSet(k, v) { try { localStorage.setItem(k, v); } catch {} }
function safeRemove(k) { try { localStorage.removeItem(k); } catch {} }
